/**
 * Bei der Registrierung angekreuzte Zustimmungen (localStorage „tk-zustimmung“), bis die Person angemeldet ist.
 * Der Eintrag hängt an der Person (Mailadresse bzw. userId) und verfällt nach MAX_AGE_MS: Auf einem gemeinsam genutzten
 * Gerät darf die Registrierung von A nie als Zustimmung von B eingetragen werden (G-WEB-4).
 */

export const PENDING_KEY = "tk-zustimmung";
/** Höchstalter einer Vormerkung: eine Woche reicht für die Bestätigung per Mail. */
export const PENDING_MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000;

export interface PendingEntry {
  /** Tag des Ankreuzens (YYYY-MM-DD), verglichen mit der Fassung des Dokuments */
  acceptedOn: string;
  kinds: string[];
  /** Mailadresse der registrierenden Person, klein geschrieben */
  email: string | null;
  userId: string | null;
  /** Zeitpunkt des Ankreuzens in ms (Ablauf) */
  savedAt: number;
  /**
   * Anmeldung mit Google (Inhaber 02.10.2026): Die Mailadresse steht vor der Rückkehr von Google nicht fest. Der Eintrag gilt dann
   * nur kurz (OAUTH_MAX_AGE_MS) für die Person, die zurückkommt; danach verfällt er.
   */
  oauthUntil?: number | null;
  /** Weg der Zustimmung für den Nachweis (0182): Registrierungsformular oder Google; ältere Einträge ohne Angabe */
  method?: "registration" | "google" | null;
  /**
   * 0412 (Befund 5a): beim Ankreuzen geltende Fassungen (public.legal_versions_public). Eingetragen wird genau diese Fassung; der
   * Vergleich mit dem Tag des Ankreuzens griff nicht, wenn eine Fassung mit späterem Datum schon vorher galt (AGB vom 22.10.2026
   * seit dem 21.10.2026). Ohne Angabe (ältere Einträge, Abruf gescheitert) gilt der bisherige Vergleich.
   */
  versions?: Record<string, string> | null;
}
/** Zeitfenster für die Rückkehr von der Google-Anmeldung */
export const OAUTH_MAX_AGE_MS = 15 * 60 * 1000;
export interface PendingPerson {
  userId: string;
  email: string;
}

const norm = (email: string | null | undefined) => (email ?? "").trim().toLowerCase() || null;

/** Eintrag roh lesen; ohne Person oder Zeitstempel (ältere Fassung des Eintrags) gilt er als ungültig und wird gelöscht. */
export function readPendingEntry(now = Date.now()): PendingEntry | null {
  try {
    const v = localStorage.getItem(PENDING_KEY);
    if (!v) return null;
    const p = JSON.parse(v) as Partial<PendingEntry> | null;
    const valid = !!p && typeof p.acceptedOn === "string" && Array.isArray(p.kinds) && typeof p.savedAt === "number"
      && (!!norm(p.email) || !!p.userId || (typeof p.oauthUntil === "number" && p.oauthUntil >= now)) && now - p.savedAt <= PENDING_MAX_AGE_MS && p.savedAt <= now + 60_000;
    if (!valid) {
      localStorage.removeItem(PENDING_KEY);
      return null;
    }
    return { acceptedOn: p.acceptedOn!, kinds: p.kinds!.map(String), email: norm(p.email), userId: p.userId ?? null, savedAt: p.savedAt!, oauthUntil: typeof p.oauthUntil === "number" ? p.oauthUntil : null,
      method: p.method === "google" || p.method === "registration" ? p.method : null, versions: cleanVersions(p.versions) };
  } catch {
    return null;
  }
}

/** Gehört der Eintrag dieser Person? Übereinstimmung von userId oder Mailadresse. */
export function pendingBelongsTo(entry: PendingEntry, person: PendingPerson | null, now = Date.now()): boolean {
  if (!person) return false;
  // Rückkehr von Google: wer im Zeitfenster angemeldet zurückkommt, hat eben selbst angekreuzt
  if (!entry.email && !entry.userId) return typeof entry.oauthUntil === "number" && entry.oauthUntil >= now;
  if (entry.userId) return entry.userId === person.userId;
  return !!entry.email && entry.email === norm(person.email);
}

/** Eintrag der angemeldeten Person; gehört er jemand anderem, wird er gelöscht. */
export function pendingFor(person: PendingPerson | null, now = Date.now()): PendingEntry | null {
  const e = readPendingEntry(now);
  if (!e || !person) return null;
  if (!pendingBelongsTo(e, person, now)) {
    forgetPendingConsent();
    return null;
  }
  // Rückkehr von Google: ab jetzt an diese Person binden (gilt dann wie jede Vormerkung eine Woche, z. B. für den AVV)
  if (!e.email && !e.userId) {
    const bound = { ...e, userId: person.userId, oauthUntil: null };
    writePendingEntry(bound);
    return bound;
  }
  return e;
}

export function writePendingEntry(entry: PendingEntry | null): void {
  try {
    if (entry && entry.kinds.length && (entry.email || entry.userId || entry.oauthUntil)) localStorage.setItem(PENDING_KEY, JSON.stringify(entry));
    else localStorage.removeItem(PENDING_KEY);
  } catch {
    /* ohne Speicher: die Rückfrage erscheint nach der Anmeldung */
  }
}

export function forgetPendingConsent(): void {
  try {
    localStorage.removeItem(PENDING_KEY);
  } catch {
    /* nichts zu tun */
  }
}

/** Beim Abmelden bzw. Personenwechsel: Vormerkung löschen, außer sie gehört der neu angemeldeten Person. */
export function forgetPendingConsentUnlessFor(person: PendingPerson | null): void {
  const e = readPendingEntry();
  if (e && !pendingBelongsTo(e, person)) forgetPendingConsent();
}

export { norm as normalizePendingEmail };

/** Vor der Weiterleitung zu Google: Zustimmung ohne Mailadresse kurz vormerken (OAUTH_MAX_AGE_MS). */
export function rememberOAuthConsent(kinds: readonly string[], now = Date.now(), versions: Record<string, string> | null = null): void {
  writePendingEntry({ acceptedOn: new Date(now).toLocaleDateString("sv-SE"), kinds: [...kinds], email: null, userId: null, savedAt: now, oauthUntil: now + OAUTH_MAX_AGE_MS, method: "google", versions: cleanVersions(versions) });
}

/** Fassungen je Art (nur Text im Format JJJJ-MM-TT); sonst null */
export function cleanVersions(v: unknown): Record<string, string> | null {
  if (!v || typeof v !== "object") return null;
  const out: Record<string, string> = {};
  for (const [k, x] of Object.entries(v as Record<string, unknown>)) if (/^[a-z_]{2,30}$/.test(k) && typeof x === "string" && /^\d{4}-\d{2}-\d{2}$/.test(x)) out[k] = x;
  return Object.keys(out).length ? out : null;
}

/** Hat die angemeldete Person vor der Google-Anmeldung zugestimmt (Eintrag im Zeitfenster)? */
export function oauthConsentFor(person: PendingPerson | null, now = Date.now()): boolean {
  const e = readPendingEntry(now);
  return !!e && !e.email && !e.userId && pendingBelongsTo(e, person, now);
}
